import { EnvironmentConfig } from '@/shared/config';
import { IS_WEB } from '@/shared/constants/utility';
import * as Sentry from '@sentry/react-native';

// Type for errors that might have additional properties
export interface EnhancedError extends Error {
  code?: string | number;
  status?: number;
  statusCode?: number;
  response?: {
    data?: unknown;
    status?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

class SentryService {
  private static navigationIntegration = Sentry.reactNavigationIntegration({
    enableTimeToInitialDisplay: !IS_WEB,
  });

  // Helper to safely extract error properties
  private static extractErrorProperties(error: Error): Record<string, unknown> {
    const enhancedError = error as EnhancedError;
    const extras: Record<string, unknown> = {};

    if (enhancedError.code !== undefined) {
      extras.errorCode = enhancedError.code;
    }
    if (enhancedError.status !== undefined) {
      extras.errorStatus = enhancedError.status;
    }
    if (enhancedError.statusCode !== undefined) {
      extras.errorStatusCode = enhancedError.statusCode;
    }
    if (enhancedError.response) {
      extras.errorResponse = enhancedError.response;
      if (enhancedError.response.data !== undefined) {
        extras.errorResponseData = enhancedError.response.data;
      }
      if (enhancedError.response.status !== undefined) {
        extras.errorResponseStatus = enhancedError.response.status;
      }
    }

    return extras;
  }

  // Helper to safely stringify any value for error messages
  private static safeStringify(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value instanceof Error) {
      return value.message;
    }

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  static init(environment: EnvironmentConfig) {
    Sentry.init({
      dsn: environment.sentry.dsn,
      debug: false, // Log Sentry events to console
      environment: environment.name,
      enabled: !__DEV__,
      release: IS_WEB ? process.env.EXPO_PUBLIC_SENTRY_RELEASE : undefined,
      integrations: [
        SentryService.navigationIntegration,
        Sentry.hermesProfilingIntegration({
          platformProfilers: true,
        }),
      ],
      // Capture % of transactions (network requests, screen transitions, custom transactions)
      tracesSampleRate: environment.name === 'production' ? 0.1 : 1.0,
      // Capture CPU/memory usage for % of sampled traces
      profilesSampleRate: 1.0,
    });

    // Catch unhandled JavaScript errors
    globalThis.onerror = (message, source, lineno, colno, error) => {
      // Use the actual error for proper Sentry grouping, add context in extras
      const actualError = error || new Error(String(message));
      const contextMessage = error
        ? String(message)
        : 'Unhandled JavaScript Error';

      SentryService.captureError(contextMessage, actualError, {
        isUnhandledError: true,
        originalMessage: message,
        sourceFile: source,
        lineNumber: lineno,
        columnNumber: colno,
        userAgent: globalThis.navigator?.userAgent,
        url: globalThis.location?.href,
        timestamp: new Date().toISOString(),
        errorType: 'unhandledJavaScriptError',
      });
    };

    // Catch unhandled Promise rejections
    globalThis.onunhandledrejection = (event) => {
      const reasonString = SentryService.safeStringify(event.reason);

      // Use the actual rejection reason for proper Sentry grouping
      const actualError =
        event.reason instanceof Error ? event.reason : new Error(reasonString);

      const contextMessage =
        event.reason instanceof Error
          ? 'Unhandled Promise Rejection'
          : `Unhandled Promise Rejection: ${reasonString}`;

      SentryService.captureError(contextMessage, actualError, {
        isUnhandledPromiseRejection: true,
        promiseRejectionReason: event.reason,
        promiseRejectionReasonStringified: reasonString,
        promiseRejectionHandled: event.defaultPrevented,
        userAgent: globalThis.navigator?.userAgent,
        url: globalThis.location?.href,
        timestamp: new Date().toISOString(),
        errorType: 'unhandledPromiseRejection',
        hasPromiseReference: !!event.promise,
      });
    };
  }

  static async resetSentry(environment: EnvironmentConfig) {
    try {
      await Sentry.close();
      await Sentry.flush();

      SentryService.init(environment);
    } catch (error) {
      SentryService.captureError('Error resetting Sentry', error);
    }
  }

  static registerNavigationContainer(navigationRef: unknown) {
    if (SentryService.navigationIntegration) {
      SentryService.navigationIntegration.registerNavigationContainer(
        navigationRef,
      );
    }
  }

  static captureError(
    message: string,
    error?: unknown,
    extra?: Record<string, unknown>,
  ) {
    __DEV__ && console.error(message, error, extra);

    Sentry.withScope((scope) => {
      let capturedError: Error;
      const errorExtras: Record<string, unknown> = { ...(extra || {}) };

      if (typeof error === 'string') {
        capturedError = new Error(error);
        // Capture stack trace at original call site, not here
        if (Error.captureStackTrace) {
          Error.captureStackTrace(capturedError, SentryService.captureError);
        }
        errorExtras.originalErrorType = 'string';
        errorExtras.originalErrorContent = error;
        capturedError.message = `${message}: ${error}`;
      } else if (error instanceof Error) {
        capturedError = error;
        errorExtras.originalErrorMessage = error.message;
        errorExtras.originalErrorType = 'Error';
        errorExtras.originalErrorName = error.name;
        errorExtras.originalErrorStack = error.stack;

        // Extract additional error properties using helper function
        const additionalProps = SentryService.extractErrorProperties(error);
        Object.assign(errorExtras, additionalProps);

        // Capture call site information (where SentryService.captureError was called)
        const callSiteError = new Error('Call site stack trace');
        if (Error.captureStackTrace) {
          Error.captureStackTrace(callSiteError, SentryService.captureError);
        }
        errorExtras.callSiteStack = callSiteError.stack;

        // Combine messages instead of overwriting
        capturedError.message = `${message}: ${error.message}`;
      } else if (error) {
        capturedError = new Error(message);
        // Capture stack trace at original call site, not here
        if (Error.captureStackTrace) {
          Error.captureStackTrace(capturedError, SentryService.captureError);
        }
        errorExtras.originalError = error;
        errorExtras.originalErrorType = typeof error;

        // Try to stringify the error safely
        try {
          errorExtras.originalErrorStringified = JSON.stringify(error);
        } catch {
          errorExtras.originalErrorStringified = String(error);
        }
      } else {
        capturedError = new Error(message);
        // Capture stack trace at original call site, not here
        if (Error.captureStackTrace) {
          Error.captureStackTrace(capturedError, SentryService.captureError);
        }
        errorExtras.originalErrorType = 'none';
      }

      // Set context information
      scope.setTag('errorContext', message);
      scope.setExtras(errorExtras);

      Sentry.captureException(capturedError);
    });
  }

  static captureInfo(
    message: string,
    data?: unknown,
    extra?: Record<string, unknown>,
    level: Sentry.SeverityLevel = 'info',
  ) {
    try {
      Sentry.withScope((scope) => {
        const infoExtras: Record<string, unknown> = { ...(extra || {}) };
        let finalMessage = message;

        // Handle different types of data
        if (typeof data === 'string') {
          finalMessage = `${message}: ${data}`;
          infoExtras.originalDataType = 'string';
          infoExtras.originalDataContent = data;
        } else if (data instanceof Error) {
          finalMessage = `${message}: ${data.message}`;
          infoExtras.originalDataType = 'Error';
          infoExtras.originalDataMessage = data.message;
          infoExtras.originalDataName = data.name;
          infoExtras.originalDataStack = data.stack;

          // Extract additional error properties using helper function
          const additionalProps = SentryService.extractErrorProperties(data);
          Object.assign(infoExtras, additionalProps);
        } else if (data !== undefined && data !== null) {
          infoExtras.originalData = data;
          infoExtras.originalDataType = typeof data;

          // Try to stringify the data safely
          try {
            infoExtras.originalDataStringified = JSON.stringify(data);

            // If it's an object, include a readable version in the message
            if (typeof data === 'object') {
              const dataString = SentryService.safeStringify(data);
              if (dataString !== '[object Object]') {
                finalMessage = `${message}: ${dataString}`;
              }
            } else {
              finalMessage = `${message}: ${String(data)}`;
            }
          } catch {
            const dataString = String(data);
            infoExtras.originalDataStringified = dataString;
            finalMessage = `${message}: ${dataString}`;
          }
        } else {
          infoExtras.originalDataType = data === null ? 'null' : 'undefined';
        }

        // Capture call site information (where SentryService.captureInfo was called)
        const callSiteError = new Error('Call site stack trace');
        if (Error.captureStackTrace) {
          Error.captureStackTrace(callSiteError, SentryService.captureInfo);
        }
        infoExtras.callSiteStack = callSiteError.stack;

        // Add timestamp and context
        infoExtras.timestamp = new Date().toISOString();
        infoExtras.logLevel = level;

        // Set context information
        scope.setTag('infoContext', message);
        scope.setLevel(level);
        scope.setExtras(infoExtras);

        Sentry.captureEvent({
          message: finalMessage ?? 'This is a test by Dan, the developer',
          level: level ?? 'info',
        });
      });
    } catch (error) {
      SentryService.captureError('Error capturing info', error);
    }
  }
}

export default SentryService;
